import { SupplierTags } from "@/components/suppliers/SupplierTags";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { getSupplierTags, saveSupplierTags } from "@/hooks/use-supplier-tags";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ensureProfileExists } from "@/utils/profileUtils";
import {
	AlertTriangle,
	Clock,
	Loader2,
	Package,
	Truck,
	Upload,
} from "lucide-react";
import { useEffect, useState } from "react";

export function ProfileSection() {
	const { user, profile, refreshProfile } = useAuth();
	const { toast } = useToast();
	const [loading, setLoading] = useState(false);
	const [uploadingImage, setUploadingImage] = useState(false);
	const [formData, setFormData] = useState({
		name: "",
		business_name: "",
		location: "",
		description: "",
		minimum_free_shipping: "",
		shipping_cost: "",
		shipping_cost_type: "fixed",
		minimum_order: "",
		min_delivery_days: "",
		max_delivery_days: "",
		has_minimum_free_shipping: false,
		has_minimum_order: false,
		website: "",
	});
	const [tags, setTags] = useState<string[]>([]);

	useEffect(() => {
		if (profile?.id) {
			loadTags();
		}
	}, [profile?.id]);

	useEffect(() => {
		if (profile) {
			setFormData({
				name: profile.name || "",
				business_name: profile.businessName || "",
				location: profile.location || "",
				description: profile.description || "",
				minimum_free_shipping:
					profile.minimumFreeShipping?.toString() || "",
				shipping_cost: profile.shippingCost?.toString() || "",
				shipping_cost_type: profile.shippingCostType || "fixed",
				minimum_order: profile.minimumOrder?.toString() || "",
				min_delivery_days: profile.minDeliveryDays?.toString() || "",
				max_delivery_days: profile.maxDeliveryDays?.toString() || "",
				has_minimum_free_shipping: profile.minimumFreeShipping > 0,
				has_minimum_order: profile.minimumOrder > 0,
				website: profile.website || "",
			});
		}
	}, [profile]);

	const loadTags = async () => {
		if (!profile?.id) return;
		try {
			const supplierTags = await getSupplierTags(profile.id);
			setTags(supplierTags.map((tag) => tag.id));
		} catch (error) {
			console.error("Error loading tags:", error);
		}
	};

	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target;

		if (
			name === "minimum_free_shipping" ||
			name === "shipping_cost" ||
			name === "minimum_order" ||
			name === "min_delivery_days" ||
			name === "max_delivery_days"
		) {
			setFormData((prev) => ({
				...prev,
				[name]: value,
			}));
		} else {
			setFormData((prev) => ({ ...prev, [name]: value }));
		}
	};

	const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, checked } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: checked,
			...(name === "has_minimum_free_shipping" &&
				!checked && { minimum_free_shipping: "" }),
			...(name === "has_minimum_order" &&
				!checked && { minimum_order: "" }),
		}));
	};

	const handleRadioChange = (value: string) => {
		setFormData((prev) => ({ ...prev, shipping_cost_type: value }));
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!user) return;

		const requiredFields = [
			"name",
			"business_name",
			"location",
			"description",
			"shipping_cost",
			"min_delivery_days",
			"max_delivery_days",
		];
		const missingFields = requiredFields.filter(
			(field) => !formData[field as keyof typeof formData]
		);

		if (missingFields.length > 0) {
			toast({
				title: "Missing Required Fields",
				description: "Please fill in all required fields.",
				variant: "destructive",
			});
			return;
		}

		try {
			setLoading(true);

			const { error } = await supabase
				.from("profiles")
				.update({
					name: formData.name,
					business_name: formData.business_name,
					location: formData.location,
					description: formData.description,
					minimum_free_shipping: formData.has_minimum_free_shipping
						? parseFloat(formData.minimum_free_shipping)
						: null,
					shipping_cost: formData.shipping_cost
						? parseFloat(formData.shipping_cost)
						: 0,
					shipping_cost_type: formData.shipping_cost_type,
					minimum_order: formData.has_minimum_order
						? parseFloat(formData.minimum_order)
						: 0,
					min_delivery_days: formData.min_delivery_days
						? parseInt(formData.min_delivery_days)
						: 5,
					max_delivery_days: formData.max_delivery_days
						? parseInt(formData.max_delivery_days)
						: 10,
					website: formData.website || null,
				})
				.eq("id", user.id);

			if (error) throw error;

			await saveSupplierTags(user.id, tags);

			await refreshProfile();

			toast({
				title: "Profile updated",
				description: "Your profile has been updated successfully.",
			});
		} catch (error: any) {
			toast({
				title: "Error",
				description:
					error.message ||
					"Failed to update profile. Please try again.",
				variant: "destructive",
			});
		} finally {
			setLoading(false);
		}
	};

	const handleImageUpload = async (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		const file = e.target.files?.[0];
		if (!file || !user) return;

		if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
			toast({
				title: "Invalid file type",
				description: "Please upload a JPEG, PNG, or WebP image.",
				variant: "destructive",
			});
			return;
		}

		if (file.size > 5 * 1024 * 1024) {
			toast({
				title: "File too large",
				description: "Please upload an image smaller than 5MB.",
				variant: "destructive",
			});
			return;
		}

		try {
			setUploadingImage(true);

			const profileCreated = await ensureProfileExists(
				user.id,
				"supplier"
			);

			if (!profileCreated) {
				throw new Error("Failed to create or find supplier profile");
			}

			const fileExt = file.name.split(".").pop();
			const fileName = `${user.id}-${Date.now()}.${fileExt}`;

			const { error: uploadError } = await supabase.storage
				.from("profile_images")
				.upload(fileName, file, { upsert: true });

			if (uploadError) throw uploadError;

			const { data: publicURL } = supabase.storage
				.from("profile_images")
				.getPublicUrl(fileName);

			const { error: updateError } = await supabase
				.from("profiles")
				.update({ profile_image: publicURL.publicUrl })
				.eq("id", user.id);

			if (updateError) {
				console.error("Error updating profile image:", updateError);
				throw updateError;
			}

			await refreshProfile();

			toast({
				title: "Profile image updated",
				description:
					"Your profile image has been updated successfully.",
			});
		} catch (error: any) {
			console.error("Error uploading profile image:", error);
			toast({
				title: "Error",
				description:
					error.message ||
					"Failed to upload profile image. Please try again.",
				variant: "destructive",
			});
		} finally {
			setUploadingImage(false);
		}
	};

	const isSupplier = profile?.accountType === "supplier";

	return (
		<div className="space-y-6">
			<div>
				<h2 className="text-2xl font-semibold tracking-tight">
					Your Profile
				</h2>
				<p className="text-gray-500">
					Update your profile information and business details.
				</p>
			</div>

			<div className="bg-white border border-gray-100 rounded-lg shadow-sm">
				<div className="p-6">
					<div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center mb-6">
						<div className="relative">
							<Avatar className="h-24 w-24">
								<AvatarImage
									src={profile?.profileImage || undefined}
									alt={profile?.name || ""}
								/>
								<AvatarFallback className="text-2xl bg-primary/10 text-primary">
									{(profile?.name || "")
										.split(" ")
										.map((n) => n[0])
										.join("")
										.toUpperCase()
										.slice(0, 2)}
								</AvatarFallback>
							</Avatar>

							<label
								htmlFor="profile-image"
								className="absolute -bottom-2 -right-2 bg-primary text-white p-1.5 rounded-full cursor-pointer hover:bg-primary/90 transition-colors">
								{uploadingImage ? (
									<Loader2 className="h-4 w-4 animate-spin" />
								) : (
									<Upload className="h-4 w-4" />
								)}
							</label>
							<input
								id="profile-image"
								type="file"
								accept="image/jpeg, image/png, image/webp"
								className="hidden"
								onChange={handleImageUpload}
								disabled={uploadingImage || loading}
							/>
						</div>

						<div>
							<h3 className="text-lg font-medium">
								{profile?.name || profile?.businessName}
							</h3>
							<p className="text-gray-500 text-sm">
								{profile?.accountType === "supplier"
									? "Supplier"
									: "Retailer"}
							</p>
						</div>
					</div>

					<form onSubmit={handleSubmit}>
						<div className="space-y-4">
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label htmlFor="name">Your Name</Label>
									<Input
										id="name"
										name="name"
										value={formData.name}
										onChange={handleInputChange}
										placeholder="John Doe"
									/>
								</div>

								<div className="space-y-2">
									<Label htmlFor="business_name">
										Business Name
									</Label>
									<Input
										id="business_name"
										name="business_name"
										value={formData.business_name}
										onChange={handleInputChange}
										placeholder="Acme Corporation"
									/>
									<p className="text-xs text-gray-500">
										This will be displayed to customers as
										your brand name.
									</p>
								</div>
							</div>

							<div className="space-y-2">
								<Label htmlFor="website">
									Website (optional)
								</Label>
								<Input
									id="website"
									name="website"
									value={formData.website}
									onChange={handleInputChange}
									placeholder="https://yourwebsite.com"
								/>
							</div>

							<div className="space-y-2">
								<Label htmlFor="location">Location</Label>
								<Input
									id="location"
									name="location"
									value={formData.location}
									onChange={handleInputChange}
									placeholder="New York, USA"
								/>
							</div>

							{isSupplier && (
								<>
									<div className="space-y-2">
										<Label>Tags</Label>
										<SupplierTags
											value={tags}
											onChange={setTags}
											editable
											placeholder="Add tags to help retailers find you..."
										/>
										<p className="text-xs text-gray-500">
											Add tags that describe your
											business, products, or specialties.
										</p>
									</div>

									<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
										<div className="space-y-2">
											<Label
												htmlFor="min_delivery_days"
												className="flex items-center gap-2">
												<Clock className="h-4 w-4" />
												Estimated Minimum Delivery Days
												range
											</Label>
											<Input
												id="min_delivery_days"
												name="min_delivery_days"
												type="number"
												min="1"
												step="1"
												value={
													formData.min_delivery_days
												}
												onChange={handleInputChange}
												placeholder="5"
											/>
											<p className="text-xs text-gray-500">
												The minimum number of days it
												typically takes for your orders
												to be delivered.
											</p>
										</div>

										<div className="space-y-2">
											<Label
												htmlFor="max_delivery_days"
												className="flex items-center gap-2">
												<Clock className="h-4 w-4" />
												Estimated Maximum Delivery Days
												range
											</Label>
											<Input
												id="max_delivery_days"
												name="max_delivery_days"
												type="number"
												min={formData.min_delivery_days}
												step="1"
												value={
													formData.max_delivery_days
												}
												onChange={handleInputChange}
												placeholder="10"
											/>
											<p className="text-xs text-gray-500">
												The maximum number of days it
												might take for your orders to be
												delivered.
											</p>
										</div>
									</div>

									<div className="space-y-2">
										<div className="flex items-center gap-2 mb-2">
											<input
												type="checkbox"
												className="accent-accent"
												id="has_minimum_free_shipping"
												name="has_minimum_free_shipping"
												checked={
													formData.has_minimum_free_shipping
												}
												onChange={handleCheckboxChange}
											/>
											<Label
												htmlFor="has_minimum_free_shipping"
												className="flex items-center gap-2">
												<Truck className="h-4 w-4" />
												Enable Minimum Order for Free
												Shipping
											</Label>
										</div>
										{formData.has_minimum_free_shipping && (
											<Input
												id="minimum_free_shipping"
												name="minimum_free_shipping"
												type="number"
												step="0.01"
												value={
													formData.minimum_free_shipping
												}
												onChange={handleInputChange}
												placeholder="Enter minimum order amount for free shipping"
												required
											/>
										)}
									</div>

									<div className="space-y-4">
										<Label
											htmlFor="shipping_cost_type"
											className="flex items-center gap-2">
											<Package className="h-4 w-4" />
											Shipping Cost Type
										</Label>

										<RadioGroup
											value={formData.shipping_cost_type}
											onValueChange={handleRadioChange}
											className="flex flex-col space-y-2">
											<div className="flex items-center space-x-2">
												<RadioGroupItem
													value="fixed"
													id="fixed"
												/>
												<Label htmlFor="fixed">
													Fixed Amount ($)
												</Label>
											</div>
											<div className="flex items-center space-x-2">
												<RadioGroupItem
													value="percentage"
													id="percentage"
												/>
												<Label htmlFor="percentage">
													Percentage of Order (%)
												</Label>
											</div>
										</RadioGroup>

										<div className="space-y-2">
											<Label htmlFor="shipping_cost">
												{formData.shipping_cost_type ===
												"fixed"
													? "Standard Shipping Cost ($)"
													: "Shipping Fee Percentage (%)"}
											</Label>
											<Input
												id="shipping_cost"
												name="shipping_cost"
												type="number"
												min="0"
												step="0.01"
												value={formData.shipping_cost}
												onChange={handleInputChange}
												placeholder={
													formData.shipping_cost_type ===
													"fixed"
														? "10.00"
														: "5.0"
												}
											/>
											<p className="text-xs text-gray-500">
												{formData.shipping_cost_type ===
												"fixed"
													? "This is the fixed shipping fee charged for orders less than your minimum free shipping threshold."
													: "This is the percentage of the order total that will be charged as shipping for orders less than your minimum free shipping threshold."}
											</p>
										</div>
									</div>

									<div className="space-y-2">
										<div className="flex items-center gap-2 mb-2">
											<input
												type="checkbox"
												className="accent-accent"
												id="has_minimum_order"
												name="has_minimum_order"
												checked={
													formData.has_minimum_order
												}
												onChange={handleCheckboxChange}
											/>
											<Label
												htmlFor="has_minimum_order"
												className="flex items-center gap-2">
												<AlertTriangle className="h-4 w-4" />
												Enable Minimum Order Value ($)
											</Label>
										</div>
										{formData.has_minimum_order && (
											<Input
												id="minimum_order"
												name="minimum_order"
												type="number"
												step="0.01"
												value={formData.minimum_order}
												onChange={handleInputChange}
												placeholder="Enter minimum order value"
												required
											/>
										)}
									</div>
								</>
							)}

							<div className="space-y-2">
								<Label htmlFor="description">
									Business Description
								</Label>
								<Textarea
									id="description"
									name="description"
									value={formData.description}
									onChange={handleInputChange}
									placeholder="Tell potential customers about your business, products, and what makes you unique."
									rows={5}
								/>
							</div>
						</div>

						<div className="mt-6">
							<Button
								type="submit"
								variant="accent"
								disabled={loading || uploadingImage}>
								{loading ? (
									<>
										<Loader2 className="mr-2 h-4 w-4 animate-spin" />
										Saving...
									</>
								) : (
									"Save Changes"
								)}
							</Button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
}
